// Screen care for the wall panel (image persistence).
//
// The kiosk shows a near-static layout 24/7 on a small IPS LCD that the Screen
// Wake Lock keeps awake. Static content on an LCD leaves image persistence:
// reversible, but it builds up for as long as the same pixels hold the same
// value. Three measures, all derived from the clock so a reload (new build,
// Chromium relaunch) lands in the right state with no stored schedule:
//  - orbit: the layout drifts by a few pixels, so edges never sit on the same
//    pixel row/column for long;
//  - nightly rest: a cleaning cycle of full-screen fields, then a black screen
//    until morning (an IPS cell showing black is undriven, i.e. resting);
//  - manual cleaning: the same cycle on demand, to clear marks already there.

export const ORBIT_RADIUS_PX = 3
export const ORBIT_STEP_MS = 2 * 60_000
// A closed ring: each step moves one notch, so the drift is never a jump.
const ORBIT_RING = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
] as const

export function orbitOffset(nowMs: number): { x: number; y: number } {
  const [x, y] = ORBIT_RING[Math.floor(nowMs / ORBIT_STEP_MS) % ORBIT_RING.length]
  return { x: x * ORBIT_RADIUS_PX, y: y * ORBIT_RADIUS_PX }
}

// Nightly rest window, device-local wall clock. May wrap midnight.
export const REST_START_MIN = 1 * 60
export const REST_END_MIN = 6 * 60
/** Length of the cleaning cycle (nightly and manual). */
export const CLEAN_MS = 20 * 60_000
/** How long a touch brings the dashboard back during the rest window. */
export const WAKE_MS = 5 * 60_000

// Full white and black plus each primary, so every sub-pixel is driven to both
// extremes. Pure values on purpose: this is a panel exercise, not UI colour.
// Fields hold 15 s and cross-fade — never a flash.
export const FIELD_MS = 15_000
export const CLEAN_FIELDS = ['#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff'] as const

export type CareState = 'off' | 'clean' | 'rest'

/** Milliseconds since the rest window opened, or null outside it. */
export function restElapsedMs(now: Date): number | null {
  const minutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60
  const length = (REST_END_MIN - REST_START_MIN + 1440) % 1440
  const since = (minutes - REST_START_MIN + 1440) % 1440
  return since < length ? since * 60_000 : null
}

export function scheduledCare(now: Date): CareState {
  const elapsed = restElapsedMs(now)
  if (elapsed == null) return 'off'
  return elapsed < CLEAN_MS ? 'clean' : 'rest'
}

export function cleanField(elapsedMs: number): string {
  return CLEAN_FIELDS[Math.floor(Math.max(elapsedMs, 0) / FIELD_MS) % CLEAN_FIELDS.length]
}

/** Where the small label / clock sits, as % of the screen. Moves every minute
 *  along a low-discrepancy sequence so the overlay's own text cannot mark. */
export function careAnchor(nowMs: number): { left: number; top: number } {
  const i = Math.floor(nowMs / 60_000)
  const frac = (v: number) => v - Math.floor(v)
  return {
    left: 6 + frac(i * 0.6180339887) * 68,
    top: 12 + frac(i * 0.4142135623) * 56,
  }
}
